/* =============================================
   URBAN UNSEEN — FEATURES.JS
   Feature 1: AJAX Cart Drawer
   Feature 2: Predictive Search
   Feature 3: AJAX Collection Filtering
   Feature 4: Instagram Feed (CSS-driven)
   Feature 5: Email Popup
   ============================================= */

'use strict';

/* =============================================
   UTILITY: Format money (Shopify cents → display)
   ============================================= */
const formatMoney = (cents) => {
  const amount = (cents / 100).toFixed(2);
  return `$${amount}`;
};

/* =============================================
   UTILITY: Debounce
   ============================================= */
const debounce = (fn, delay) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
};

/* =============================================
   UTILITY: Lock / unlock body scroll
   ============================================= */
const lockScroll  = () => { document.body.style.overflow = 'hidden'; };
const unlockScroll = () => { document.body.style.overflow = ''; };

/* =============================================
   FEATURE 1: AJAX CART DRAWER
   ============================================= */
class CartDrawer {
  constructor() {
    this.drawer   = document.getElementById('cart-drawer');
    this.overlay  = document.getElementById('cart-overlay');
    this.closeBtn = document.getElementById('cart-drawer-close');
    this.trigger  = document.getElementById('cart-trigger');
    this.mobileTrigger = document.getElementById('mobile-cart-trigger');
    this.itemsEl  = document.getElementById('cart-drawer-items');
    this.emptyEl  = document.getElementById('cart-drawer-empty');
    this.footerEl = document.getElementById('cart-drawer-footer');
    this.subtotal = document.getElementById('cart-drawer-subtotal');
    this.countEl  = document.getElementById('cart-count');

    if (!this.drawer) return;

    this._bindEvents();
    this._interceptAddToCart();
  }

  _bindEvents() {
    // Open triggers
    this.trigger?.addEventListener('click', () => this.open());
    this.mobileTrigger?.addEventListener('click', () => this.open());

    // Close
    this.closeBtn?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());

    // Keyboard ESC
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) this.close();
    });
  }

  open() {
    this.drawer.classList.add('is-open');
    this.overlay.classList.add('is-active');
    this.drawer.setAttribute('aria-hidden', 'false');
    lockScroll();
    this.fetchCart();
  }

  close() {
    this.drawer.classList.remove('is-open');
    this.overlay.classList.remove('is-active');
    this.drawer.setAttribute('aria-hidden', 'true');
    unlockScroll();
  }

  async fetchCart() {
    // Show loading spinner in items area
    this.itemsEl.innerHTML = `
      <div class="cart-drawer__loading">
        <div class="cart-spinner"></div>
        LOADING...
      </div>`;

    try {
      const res  = await fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } });
      const cart = await res.json();
      this._renderCart(cart);
    } catch (err) {
      console.error('[CartDrawer] fetchCart error:', err);
      this.itemsEl.innerHTML = `<p style="padding:32px;text-align:center;font-family:var(--font-tech);font-size:10px;letter-spacing:0.2em;color:rgba(192,192,192,0.3);">COULD NOT LOAD CART. PLEASE REFRESH.</p>`;
    }
  }

  _renderCart(cart) {
    // Update count badge
    this._updateCount(cart.item_count);

    if (cart.item_count === 0) {
      this.itemsEl.innerHTML = '';
      this.emptyEl.style.display  = 'flex';
      this.footerEl.style.display = 'none';
      return;
    }

    this.emptyEl.style.display  = 'none';
    this.footerEl.style.display = 'block';

    // Subtotal
    this.subtotal.textContent = formatMoney(cart.total_price);

    // Render items
    this.itemsEl.innerHTML = cart.items.map((item, idx) => `
      <div class="cart-drawer__item" style="animation-delay:${idx * 0.05}s">
        <div>
          ${item.image
            ? `<img class="cart-drawer__item-img" src="${item.image}" alt="${this._esc(item.title)}" loading="lazy">`
            : `<div class="cart-drawer__item-img-placeholder"><img src="/cdn/shop/files/logo.png" style="width:60%;opacity:0.15;object-fit:contain;" alt=""></div>`
          }
        </div>
        <div class="cart-drawer__item-info">
          <p class="cart-drawer__item-brand">URBAN UNSEEN</p>
          <h4 class="cart-drawer__item-name">${this._esc(item.product_title)}</h4>
          ${item.variant_title && item.variant_title !== 'Default Title'
            ? `<p class="cart-drawer__item-variant">${this._esc(item.variant_title)}</p>`
            : ''}
          <div class="cart-drawer__item-bottom">
            <span class="cart-drawer__item-price">${formatMoney(item.final_line_price)}</span>
            <div class="cart-drawer__qty">
              <button class="cart-drawer__qty-btn" data-action="decrease" data-key="${item.key}" data-qty="${item.quantity}" aria-label="Decrease quantity">−</button>
              <span class="cart-drawer__qty-num">${item.quantity}</span>
              <button class="cart-drawer__qty-btn" data-action="increase" data-key="${item.key}" data-qty="${item.quantity}" aria-label="Increase quantity">+</button>
            </div>
          </div>
          <button class="cart-drawer__item-remove" data-key="${item.key}">REMOVE</button>
        </div>
      </div>
    `).join('');

    // Bind qty + remove buttons
    this.itemsEl.querySelectorAll('.cart-drawer__qty-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const action = btn.dataset.action;
        const key    = btn.dataset.key;
        const qty    = parseInt(btn.dataset.qty, 10);
        const newQty = action === 'increase' ? qty + 1 : qty - 1;
        this.updateItem(key, newQty);
      });
    });

    this.itemsEl.querySelectorAll('.cart-drawer__item-remove').forEach(btn => {
      btn.addEventListener('click', () => this.updateItem(btn.dataset.key, 0));
    });
  }

  async updateItem(key, quantity) {
    try {
      const res  = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      const cart = await res.json();
      this._renderCart(cart);
    } catch (err) {
      console.error('[CartDrawer] updateItem error:', err);
    }
  }

  _updateCount(count) {
    if (!this.countEl) return;
    this.countEl.textContent = count;
    // Bounce animation
    this.countEl.style.transform = 'scale(1.6)';
    this.countEl.style.background = '#ffffff';
    setTimeout(() => { this.countEl.style.transform = ''; }, 350);
  }

  _interceptAddToCart() {
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('form[action="/cart/add"]');
      if (!form) return;
      e.preventDefault();

      const submitBtn = form.querySelector('[type="submit"], .add-to-cart-btn');
      const originalText = submitBtn?.innerHTML;

      // Button feedback
      if (submitBtn) {
        submitBtn.innerHTML = 'ADDING...';
        submitBtn.disabled  = true;
      }

      try {
        const formData = new FormData(form);
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          body: formData
        });

        if (!res.ok) throw new Error('Add to cart failed');

        if (submitBtn) {
          submitBtn.innerHTML  = '✓ ADDED TO CART';
          submitBtn.style.background = '#1a1a1a';
          submitBtn.style.color = '#fff';
        }

        // Open cart drawer
        setTimeout(() => this.open(), 200);

        // Reset button
        setTimeout(() => {
          if (submitBtn) {
            submitBtn.innerHTML  = originalText;
            submitBtn.style.background = '';
            submitBtn.style.color = '';
            submitBtn.disabled   = false;
          }
        }, 2500);

      } catch (err) {
        console.error('[CartDrawer] addToCart error:', err);
        if (submitBtn) {
          submitBtn.innerHTML  = 'ERROR — TRY AGAIN';
          submitBtn.style.background = '#cc0000';
          submitBtn.style.color = '#fff';
          submitBtn.disabled   = false;
          setTimeout(() => {
            submitBtn.innerHTML  = originalText;
            submitBtn.style.background = '';
            submitBtn.style.color = '';
          }, 2500);
        }
      }
    });
  }

  _esc(str) {
    return String(str).replace(/[&<>"']/g, c =>
      ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c])
    );
  }
}

/* =============================================
   FEATURE 2: PREDICTIVE SEARCH
   ============================================= */
class PredictiveSearch {
  constructor() {
    this.modal       = document.getElementById('search-modal');
    this.overlay     = document.getElementById('search-overlay');
    this.input       = document.getElementById('predictive-search-input');
    this.clearBtn    = document.getElementById('search-clear');
    this.closeBtn    = document.getElementById('search-modal-close');
    this.triggers    = document.querySelectorAll('#search-trigger, #mobile-search-trigger');
    this.loading     = document.getElementById('search-loading');
    this.resultsGrid = document.getElementById('search-results-grid');
    this.emptyState  = document.getElementById('search-empty');
    this.defaultState = document.getElementById('search-default');
    this.logoSrc     = document.querySelector('.logo-img')?.src || '';

    if (!this.modal) return;

    this._bindEvents();
  }

  _bindEvents() {
    // Open
    this.triggers.forEach(t => t.addEventListener('click', () => this.open()));

    // Close
    this.closeBtn?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());

    // ESC
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('is-open')) this.close();
    });

    // Debounced input
    this.input?.addEventListener('input', debounce((e) => {
      const q = e.target.value.trim();
      this.clearBtn.style.display = q ? 'flex' : 'none';
      if (q.length >= 2) {
        this.search(q);
      } else {
        this._showDefault();
      }
    }, 300));

    // Clear
    this.clearBtn?.addEventListener('click', () => {
      this.input.value = '';
      this.clearBtn.style.display = 'none';
      this.input.focus();
      this._showDefault();
    });

    // Popular tag clicks
    document.querySelectorAll('.search-tag').forEach(tag => {
      tag.addEventListener('click', () => {
        this.input.value = tag.dataset.query;
        this.clearBtn.style.display = 'flex';
        this.search(tag.dataset.query);
      });
    });
  }

  open() {
    this.modal.classList.add('is-open');
    this.overlay.classList.add('is-active');
    this.modal.setAttribute('aria-hidden', 'false');
    lockScroll();
    setTimeout(() => this.input?.focus(), 400);
  }

  close() {
    this.modal.classList.remove('is-open');
    this.overlay.classList.remove('is-active');
    this.modal.setAttribute('aria-hidden', 'true');
    unlockScroll();
    // Reset
    if (this.input) this.input.value = '';
    if (this.clearBtn) this.clearBtn.style.display = 'none';
    this._showDefault();
  }

  async search(query) {
    this._showLoading();

    try {
      const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last`;
      const res  = await fetch(url, { headers: { 'Content-Type': 'application/json' } });
      const data = await res.json();

      const products = data?.resources?.results?.products || [];

      if (products.length === 0) {
        this._showEmpty();
      } else {
        this._renderResults(products);
      }
    } catch (err) {
      console.error('[PredictiveSearch] error:', err);
      this._showEmpty();
    }
  }

  _renderResults(products) {
    this.loading.style.display     = 'none';
    this.emptyState.style.display  = 'none';
    this.defaultState.style.display = 'none';

    this.resultsGrid.innerHTML = products.map(p => {
      const imgSrc = p.featured_image?.url
        ? p.featured_image.url.replace(/(\.\w+)$/, '_400x533$1')
        : null;
      const price = p.price ? formatMoney(parseInt(p.price, 10)) : '';

      return `
        <a href="${p.url}" class="search-result-item">
          <div class="search-result-img">
            ${imgSrc
              ? `<img src="${imgSrc}" alt="${p.title}" loading="lazy">`
              : `<div class="search-result-img-placeholder"><img src="${this.logoSrc}" alt="Urban Unseen"></div>`
            }
          </div>
          <p class="search-result-name">${p.title}</p>
          ${price ? `<p class="search-result-price">${price}</p>` : ''}
        </a>
      `;
    }).join('');

    this.resultsGrid.style.display = 'grid';
  }

  _showLoading() {
    this.loading.style.display      = 'flex';
    this.resultsGrid.style.display  = 'none';
    this.emptyState.style.display   = 'none';
    this.defaultState.style.display = 'none';
  }

  _showEmpty() {
    this.loading.style.display      = 'none';
    this.resultsGrid.style.display  = 'none';
    this.emptyState.style.display   = 'block';
    this.defaultState.style.display = 'none';
  }

  _showDefault() {
    this.loading.style.display      = 'none';
    this.resultsGrid.style.display  = 'none';
    this.emptyState.style.display   = 'none';
    this.defaultState.style.display = 'block';
  }
}

/* =============================================
   FEATURE 3: AJAX COLLECTION FILTERING & SORTING
   ============================================= */
class CollectionFilter {
  constructor() {
    this.grid    = document.querySelector('.collection-grid');
    this.pills   = document.querySelectorAll('.filter-pill');
    this.sortSel = document.querySelector('.sort-select');

    // Create loading bar
    this.loadingBar = document.createElement('div');
    this.loadingBar.className = 'collection-loading-bar';
    document.body.appendChild(this.loadingBar);

    if (!this.grid) return;

    this._bindEvents();
  }

  _bindEvents() {
    // Filter pills
    this.pills.forEach(pill => {
      pill.addEventListener('click', () => {
        const tag   = pill.dataset.tag || pill.textContent.trim().toLowerCase();
        const base  = window.location.pathname.split('/collections/')[0];
        const coll  = window.location.pathname.split('/collections/')[1]?.split('/')[0] || 'all';

        let url;
        if (!tag || tag === 'all') {
          url = `/collections/${coll}`;
        } else {
          url = `/collections/${coll}/${tag}`;
        }

        // Carry sort param
        const currentSort = this.sortSel?.value;
        if (currentSort && currentSort !== 'featured') {
          url += `?sort_by=${currentSort}`;
        }

        this.pills.forEach(p => p.classList.remove('is-active'));
        pill.classList.add('is-active');

        this._fetchGrid(url);
      });
    });

    // Sort dropdown
    this.sortSel?.addEventListener('change', () => {
      const sortMap = {
        'FEATURED':         'manual',
        'NEWEST':           'created-descending',
        'PRICE: LOW–HIGH':  'price-ascending',
        'PRICE: HIGH–LOW':  'price-descending',
        'BEST SELLING':     'best-selling'
      };
      const sortVal = sortMap[this.sortSel.value] || 'manual';
      const base    = window.location.pathname;
      const url     = `${base}?sort_by=${sortVal}`;
      this._fetchGrid(url);
    });
  }

  async _fetchGrid(url) {
    // Visual: dim grid + show loading bar
    this.grid.classList.add('is-loading');
    this.loadingBar.classList.add('is-loading');

    try {
      const res  = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      const html = await res.text();

      const parser  = new DOMParser();
      const doc     = parser.parseFromString(html, 'text/html');
      const newGrid = doc.querySelector('.collection-grid');

      if (newGrid) {
        // Swap content
        this.grid.innerHTML = newGrid.innerHTML;

        // Rebind reveal observer on new items
        this.grid.querySelectorAll('.product-card').forEach(card => {
          card.classList.add('reveal');
          revealObserver.observe(card);
        });

        // Update browser URL without reload
        window.history.pushState({}, '', url);
      }
    } catch (err) {
      console.error('[CollectionFilter] fetch error:', err);
    } finally {
      this.grid.classList.remove('is-loading');
      this.grid.classList.add('is-loaded');
      this.loadingBar.classList.remove('is-loading');
      setTimeout(() => this.grid.classList.remove('is-loaded'), 400);
    }
  }
}

/* =============================================
   FEATURE 5: EMAIL POPUP
   ============================================= */
class EmailPopup {
  constructor() {
    this.popup    = document.getElementById('email-popup');
    this.overlay  = document.getElementById('popup-overlay');
    this.closeBtn = document.getElementById('email-popup-close');
    this.form     = document.getElementById('popup-form');
    this.input    = document.getElementById('popup-email-input');
    this.submitBtn = document.getElementById('popup-submit');
    this.btnText  = document.getElementById('popup-btn-text');
    this.error    = document.getElementById('popup-error');
    this.skipBtn  = document.getElementById('popup-skip');
    this.defaultContent = document.getElementById('popup-content-default');
    this.successContent = document.getElementById('popup-content-success');

    this.STORAGE_KEY = 'uu_popup_dismissed';
    this.DAYS        = 7;

    if (!this.popup) return;

    this._init();
  }

  _init() {
    // Check localStorage — don't show if dismissed within 7 days
    const dismissed = localStorage.getItem(this.STORAGE_KEY);
    if (dismissed) {
      const daysAgo = (Date.now() - parseInt(dismissed, 10)) / (1000 * 60 * 60 * 24);
      if (daysAgo < this.DAYS) return;
    }

    // Show after 3.5s delay
    setTimeout(() => this._show(), 3500);

    this._bindEvents();
  }

  _show() {
    this.popup.classList.add('is-open');
    this.overlay.classList.add('is-active');
    this.popup.setAttribute('aria-hidden', 'false');
    lockScroll();
  }

  _dismiss() {
    this.popup.classList.remove('is-open');
    this.overlay.classList.remove('is-active');
    this.popup.setAttribute('aria-hidden', 'true');
    unlockScroll();
    localStorage.setItem(this.STORAGE_KEY, Date.now().toString());
  }

  _bindEvents() {
    this.closeBtn?.addEventListener('click', () => this._dismiss());
    this.overlay?.addEventListener('click', () => this._dismiss());
    this.skipBtn?.addEventListener('click',  () => this._dismiss());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.popup.classList.contains('is-open')) this._dismiss();
    });

    this.form?.addEventListener('submit', (e) => {
      e.preventDefault();
      this._handleSubmit();
    });
  }

  async _handleSubmit() {
    const email = this.input?.value.trim();

    // Validate
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.error.style.display = 'block';
      this.input.style.borderColor = 'rgba(204,0,0,0.5)';
      setTimeout(() => {
        this.error.style.display = 'none';
        this.input.style.borderColor = '';
      }, 3000);
      return;
    }

    // Loading state
    this.submitBtn.disabled  = true;
    this.btnText.textContent = 'JOINING...';

    try {
      // Submit to Shopify customer newsletter via the native newsletter endpoint
      const formData = new FormData();
      formData.append('contact[email]', email);
      formData.append('contact[tags]', 'newsletter,popup');
      formData.append('form_type', 'customer');

      await fetch('/contact', {
        method: 'POST',
        body: formData,
        headers: { 'Accept': 'application/json' }
      });

      // Show success (regardless of server response — Shopify's endpoint may redirect)
      this._showSuccess();

      // Mark as dismissed
      localStorage.setItem(this.STORAGE_KEY, Date.now().toString());

    } catch (err) {
      // Show success anyway (email captured, network may have blocked redirect)
      this._showSuccess();
    }
  }

  _showSuccess() {
    this.defaultContent.style.display = 'none';
    this.successContent.style.display  = 'block';

    // Auto-dismiss after 8s
    setTimeout(() => this._dismiss(), 8000);
  }
}

/* =============================================
   SHARED: Scroll Reveal Observer
   (exported so CollectionFilter can use it)
   ============================================= */
const revealObserver = new IntersectionObserver((entries) => {
  entries.forEach((entry, i) => {
    if (entry.isIntersecting) {
      setTimeout(() => entry.target.classList.add('visible'), i * 55);
      revealObserver.unobserve(entry.target);
    }
  });
}, { threshold: 0.08, rootMargin: '0px 0px -48px 0px' });

/* =============================================
   INIT — boot everything on DOMContentLoaded
   ============================================= */
document.addEventListener('DOMContentLoaded', () => {

  // Init all features
  const cartDrawer      = new CartDrawer();
  const predictiveSearch = new PredictiveSearch();
  const collectionFilter = new CollectionFilter();
  const emailPopup       = new EmailPopup();

  // Init scroll reveal
  document.querySelectorAll('.reveal').forEach(el => revealObserver.observe(el));

  // Footer newsletter
  const footerBtn   = document.getElementById('footer-newsletter-btn');
  const footerInput = document.getElementById('footer-email-input');

  footerBtn?.addEventListener('click', async () => {
    const email = footerInput?.value.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      footerInput.style.borderBottom = '1px solid rgba(204,0,0,0.5)';
      setTimeout(() => { footerInput.style.borderBottom = ''; }, 2000);
      return;
    }

    footerBtn.textContent = '...';
    footerBtn.disabled    = true;

    try {
      const fd = new FormData();
      fd.append('contact[email]', email);
      fd.append('form_type', 'customer');
      await fetch('/contact', { method: 'POST', body: fd });
    } catch (_) {}

    footerBtn.textContent = '✓';
    footerBtn.style.background = '#111';
    footerBtn.style.color      = '#fff';
    footerInput.value          = '';
    footerInput.placeholder    = 'WELCOME TO THE UNSEEN';

    setTimeout(() => {
      footerBtn.textContent      = '→';
      footerBtn.style.background = '';
      footerBtn.style.color      = '';
      footerInput.placeholder    = 'YOUR EMAIL';
      footerBtn.disabled         = false;
    }, 3500);
  });

  // Custom cursor
  const cursor   = document.getElementById('cursor');
  const follower = document.getElementById('cursor-follower');

  if (cursor && follower) {
    let mx = 0, my = 0, fx = 0, fy = 0;

    document.addEventListener('mousemove', (e) => {
      mx = e.clientX; my = e.clientY;
      cursor.style.left = mx + 'px';
      cursor.style.top  = my + 'px';
    });

    const animateCursor = () => {
      fx += (mx - fx) * 0.12;
      fy += (my - fy) * 0.12;
      follower.style.left = fx + 'px';
      follower.style.top  = fy + 'px';
      requestAnimationFrame(animateCursor);
    };
    animateCursor();

    document.querySelectorAll('a, button, .product-card, .category-card, .insta-feed__item').forEach(el => {
      el.addEventListener('mouseenter', () => { cursor.classList.add('hovering'); follower.classList.add('hovering'); });
      el.addEventListener('mouseleave', () => { cursor.classList.remove('hovering'); follower.classList.remove('hovering'); });
    });
  }

  // Header scroll
  const header = document.getElementById('site-header');
  if (header) {
    window.addEventListener('scroll', () => {
      header.classList.toggle('scrolled', window.scrollY > 50);
    }, { passive: true });
  }

  // Mobile hamburger
  const hamburger  = document.getElementById('hamburger');
  const mobileMenu = document.getElementById('mobile-menu');

  hamburger?.addEventListener('click', () => {
    const isOpen = mobileMenu.classList.toggle('active');
    const spans  = hamburger.querySelectorAll('span');
    spans[0].style.transform = isOpen ? 'rotate(45deg) translate(5px, 5px)' : '';
    spans[1].style.opacity   = isOpen ? '0' : '';
    spans[2].style.transform = isOpen ? 'rotate(-45deg) translate(5px, -5px)' : '';
  });

  // Product card tilt on mousemove
  document.querySelectorAll('.product-card').forEach(card => {
    card.addEventListener('mousemove', (e) => {
      const r = card.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width  - 0.5;
      const y = (e.clientY - r.top)  / r.height - 0.5;
      card.style.transform = `perspective(900px) rotateY(${x * 3}deg) rotateX(${-y * 3}deg)`;
    });
    card.addEventListener('mouseleave', () => { card.style.transform = ''; });
  });

  // Filter btn active state (homepage)
  document.querySelectorAll('.filter-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.filter-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
    });
  });

  // Star field for hero
  const heroStars = document.querySelector('.hero-stars');
  if (heroStars) {
    for (let i = 0; i < 80; i++) {
      const star = document.createElement('div');
      star.className = 'star';
      const size = Math.random() * 3 + 1;
      star.style.cssText = `width:${size}px;height:${size}px;left:${Math.random()*100}%;top:${Math.random()*100}%;--duration:${Math.random()*3+2}s;--delay:${Math.random()*4}s;opacity:${Math.random()*0.5+0.2}`;
      heroStars.appendChild(star);
    }
  }

  // Page loader
  const loaderLogo = document.querySelector('.logo-img')?.src || '';
  const loader     = document.createElement('div');
  loader.className = 'page-loader';
  loader.innerHTML = `
    <img class="loader-logo" src="${loaderLogo}" alt="Urban Unseen" style="background:none;">
    <div class="loader-bar-wrap"><div class="loader-bar"></div></div>
    <p style="font-family:'Orbitron',sans-serif;font-size:9px;letter-spacing:0.4em;color:rgba(192,192,192,0.25);margin-top:16px;">!UNSEEN AND BEYOND!</p>
  `;
  document.body.prepend(loader);

  window.addEventListener('load', () => {
    setTimeout(() => {
      loader.classList.add('hidden');
      setTimeout(() => loader.remove(), 700);
    }, 600);
  });

});
